import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'src/shared/components/ui/popups/dialog'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useCreateSupportTicket } from 'src/shared/features/support/api/use-create-support-ticket'
import { useSendSentryBug } from 'src/shared/features/support/api/use-send-sentry-bug'
import type { SupportTicketCreate, SupportType } from 'src/shared/schemas/forms/support'
import { supportTicketCreateSchema, supportTypeMap } from 'src/shared/schemas/forms/support'

interface SupportContentProps {
  onClose?: () => void
  email?: string
}

// Add array of support types that should trigger Sentry reports
const SENTRY_REPORT_TYPES: SupportType[] = [
  'account_issues',
  'referral_issues',
  'general_feedback',
  'technical_support',
]

export const SupportContent = ({ onClose, email }: SupportContentProps) => {
  const navigate = useNavigate()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const createTicket = useCreateSupportTicket()
  const sendBug = useSendSentryBug()
  const { currentAuth } = useAuthState()
  const isLoading = createTicket.isPending || sendBug.isPending
  const form = useForm<SupportTicketCreate>({
    resolver: zodResolver(supportTicketCreateSchema),
    defaultValues: {
      type: 'technical_support',
      email: email || undefined,
      description: '',
    },
  })

  const handleSubmit = async (data: SupportTicketCreate) => {
    try {
      if (isLoading) return

      // Create support ticket
      await createTicket.mutateAsync(data)

      // If support type requires Sentry report, send it
      if (SENTRY_REPORT_TYPES.includes(data.type)) {
        await sendBug.mutateAsync({
          name: currentAuth?.username || undefined,
          email: currentAuth?.email || undefined,
          message: `[${data.type}] ${data.description}`,
        })
      }

      setShowConfirmation(true)
    } catch (error) {
      console.error(error)
    }
  }

  const handleTextUs = () => {
    window.open('sms:(516)7018004', '_blank')
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      navigate(-1)
    }
  }

  if (showConfirmation) {
    return (
      <Dialog open={showConfirmation} onOpenChange={() => handleClose()}>
        <DialogContent className="p-5 sm:max-w-md">
          <DialogTitle>Thank you for submitting your request!</DialogTitle>
          <DialogDescription>
            Our team will review it and get in touch with you shortly. We appreciate your patience and enthusiasm as we
            continue to improve Conrad!
          </DialogDescription>
          <Button onClick={handleClose} className="mt-5 w-full">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-[15px] text-muted xs:text-lg">Got feedback or issues? We&apos;re here to help!</p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <SelectFormField control={form.control} name="type" label="" options={supportTypeMap} />
          {!currentAuth?.email && <InputFormField control={form.control} name="email" label="Email" />}
          <TextareaFormField
            control={form.control}
            name="description"
            label=""
            minRows={3}
            maxRows={5}
            description="What is happening?"
          />
          <p className="mb-4 text-xs text-muted">
            By submitting a support request, you consent to your email and personal information being processed by
            Conrad and its support vendor.
          </p>

          <div className="flex justify-between gap-3 pt-4">
            <Button type="button" variant="ghost" className="px-0 text-[13px] xs:text-[15px]" onClick={handleTextUs}>
              <p>
                Text us at <span className="underline">(516) 701-8004</span>
              </p>
            </Button>
            <Button type="submit" size="lg" className="rounded-full px-10" isLoading={isLoading}>
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
